/* eslint-disable complexity, max-lines */
import ApplicationDetailsReview from '@/components/config_components/ApplicationDetailsReview/ApplicationDetailsReview.vue';
import HistoricalVerificationChecksView from '@/components/config_components/HistoricalVerificationChecksView/HistoricalVerificationChecksView.vue';
import ManualVerificationHistoryView from '@/components/config_components/ManualVerificationHistoryView/ManualVerificationHistoryView.vue';
import {
  build_case_gate_journey,
  build_case_form_sections,
  build_contact_rows,
  build_profile_rows,
  fetch_application_transition_history,
  fetch_configured_case_gate_ids,
  build_summary_rows,
  build_questionnaire_answers,
  build_stage_timeline,
  fetch_all_onboarding_cases,
  hydrate_case_detail,
  resolve_gate_id_from_stage_label
} from '@/services/onboarding_journey_service';
import { first_value, normalize_text } from '@/services/onboarding_data_helpers';
import {
  WORKFLOW_ACTION_STATE,
  workflowActionComputed,
  workflowActionMethods
} from '@/services/onboarding_workflow_action_service';
import {
  fetch_assigned_questions_to_assessment,
  fetch_application_risk_history,
  fetch_risk_checklist_weights,
  fetch_submitted_risk_assessment
} from '@/services/onboarding_risk_service';
import {
  tablePaginationComputed,
  tablePaginationMethods,
  tablePaginationState
} from '@/services/table_pagination_service';
import { resolve_onboarding_journey_filter_key } from '@/services/onboarding_journey_filter_service';
import {
  get_case_entity_type_id
} from '@/views/onboarding_system_views/Gates/Verification/manage_verification_helpers';
import {
  build_binding_options,
  build_case_detail_checks,
  build_load_promises,
  get_case_forms
} from '@/views/onboarding_system_views/Gates/Verification/manage_verification_load_helpers';
import {
  apply_case_manual_binding_fallback,
  build_initial_case_checks,
  build_manual_mapping_by_link,
  check_has_manual_mappings,
  enrich_manual_checks_with_run_mappings
} from '@/views/onboarding_system_views/Gates/Verification/manage_verification_manual_runtime_helpers';
import {
  build_field_run_by_question,
  decorate_review_form_section
} from '@/views/onboarding_system_views/Gates/Verification/manage_verification_manual_helpers';
import { manageVerificationResultMethods } from '@/views/onboarding_system_views/Gates/Verification/manage_verification_result_methods';
import { manageVerificationSupportMethods } from '@/views/onboarding_system_views/Gates/Verification/manage_verification_support_methods';
import {
  build_historical_manual_item,
  build_historical_manual_review_sections,
  get_manual_field_runs
} from '@/views/onboarding_system_views/Gates/Verification/historical_manual_context_helpers';
import {
  get_manual_outcome_action,
  get_manual_outcome_label,
  get_manual_outcome_tone
} from '@/views/onboarding_system_views/Gates/Verification/manage_verification_manual_helpers';
import {
  hide_onboarding_system_loader_after_render,
  show_onboarding_system_loader
} from '@/views/onboarding_system_views/onboarding_ui_helpers';

const RISK_STATES = new Map([
  ['high', { score: 85, risk_tone: 'danger', action: 'Review' }],
  ['medium', { score: 58, risk_tone: 'warning', action: 'Review' }]
]);
const DEFAULT_RISK_STATE = { score: 22, risk_tone: 'success', action: 'Auto-proceed' };
const RISK_FACTOR_KEYS = [
  'failed_rules',
  'failed_checks',
  'risk_factors',
  'factors',
  'flags',
  'findings'
];

function matches_active_journey(case_item = {}, journey_filter = '') {
  const normalized_filter = normalize_text(journey_filter);
  if (!normalized_filter) return true;

  const candidate_values = [
    case_item?.entity_type_id,
    case_item?.journey_tone,
    case_item?.journey_key,
    case_item?.journey
  ];
  return candidate_values.some((value) => normalize_text(value) === normalized_filter);
}

function get_stage_label(case_item = {}) {
  return first_value([
    case_item?.current_workflow_status,
    case_item?.workflow_status_name,
    case_item?.stage,
    case_item?.application_status,
    case_item?.status?.status,
    case_item?.status?.name,
    case_item?.status_name,
    case_item?.status,
    case_item?.detail
  ], '');
}

function is_risk_stage(case_item = {}) {
  const label = normalize_text(get_stage_label(case_item));
  return label.includes('risk') || (label.includes('policy') && label.includes('rule'));
}

function get_risk_score(case_item = {}) {
  return first_value([
    case_item?.risk_result?.total_score,
    case_item?.risk_score,
    case_item?.risk_rating_score,
    case_item?.threshold_assessment?.score,
    case_item?.threshold_assessment?.risk_score
  ], null);
}

function get_risk_result_value(case_item = {}) {
  return first_value([
    case_item?.risk_result?.result,
    case_item?.risk_result?.status,
    case_item?.threshold_assessment?.threshold_assessment,
    case_item?.threshold_assessment?.result,
    case_item?.compliance_result,
    case_item?.risk
  ], '');
}

function get_threshold_summary(case_item = {}) {
  const threshold = case_item?.threshold_assessment || {};
  if (!threshold || typeof threshold !== 'object') return '';

  const total = Number(threshold.total_associations);
  const passed = Number(threshold.passed_associations);
  const failed = Number(threshold.failed_associations);
  if (!Number.isFinite(total)) return '';

  return `${passed || 0} passed, ${failed || 0} failed, ${total || 0} total`;
}

function build_risk_summary_rows(case_item = {}, risk_item = {}) {
  const score = get_risk_score(case_item);
  const rows = [
    { label: 'Risk level', value: risk_item.risk || case_item?.risk || 'Not assessed' },
    { label: 'Risk score', value: score === null ? '-' : String(score) },
    { label: 'Result', value: get_risk_result_value(case_item) || '-' },
    { label: 'Stage', value: risk_item.stage || get_stage_label(case_item) || '-' },
    { label: 'Reviewer action', value: risk_item.action || 'Review' },
    { label: 'Threshold summary', value: get_threshold_summary(case_item) || '-' }
  ];
  return rows;
}

function normalize_risk_factor_item(item, index) {
  if (typeof item === 'string') {
    return { id: `factor-${index}`, label: item, detail: '', tone: 'warning' };
  }

  if (!item || typeof item !== 'object') {
    return { id: `factor-${index}`, label: String(item || 'Risk factor'), detail: '', tone: 'warning' };
  }

  return {
    id: String(first_value([item.id, item.rule_id, item.question_id, `factor-${index}`])),
    label: first_value([item.label, item.name, item.rule_name, item.question, item.field, item.title], 'Risk factor'),
    detail: first_value([item.reason, item.detail, item.description, item.message, item.answer], ''),
    tone: normalize_text(first_value([item.tone, item.status, item.result], '')).includes('pass') ? 'success' : 'warning'
  };
}

function collect_risk_factor_values(source = {}) {
  if (!source || typeof source !== 'object') return [];
  const values = RISK_FACTOR_KEYS.flatMap((key) => {
    const value = Reflect.get(source, key);
    if (Array.isArray(value)) return value;
    return value ? [value] : [];
  });
  return values;
}

function build_risk_factors(case_item = {}) {
  const sources = [
    case_item,
    case_item?.risk_result,
    case_item?.threshold_assessment
  ];
  const factors = sources.flatMap(collect_risk_factor_values);
  return factors.map(normalize_risk_factor_item);
}

function get_status_risk_items(case_item = {}) {
  const risk_state = case_item?.onboarding_status?.risk_assessments || {};
  if (Array.isArray(risk_state?.items)) return risk_state.items;
  if (Array.isArray(risk_state)) return risk_state;
  return [];
}

function resolve_created_assessment(case_item = {}) {
  const risk_result = case_item?.risk_result || {};
  const assessment_id = Number(first_value([
    risk_result?.assessment_id,
    risk_result?.risk_framework_assessment_id,
    case_item?.assessment_id,
    get_status_risk_items(case_item)?.[0]?.id
  ], 0));
  const status_item = get_status_risk_items(case_item).find((item) => (
    Number(first_value([item?.id, item?.risk_framework_assessment_id], 0)) === assessment_id
  )) || get_status_risk_items(case_item)[0] || {};

  if (!assessment_id) return null;

  return {
    id: assessment_id,
    name: first_value([
      risk_result?.assessment_name,
      status_item?.assessment_name,
      status_item?.name,
      status_item?.risk_assessment
    ], 'Risk assessment')
  };
}

function normalize_answer_values(answer) {
  if (Array.isArray(answer)) return answer.map((item) => String(item || '').trim()).filter(Boolean);
  const normalized = String(answer ?? '').trim();
  return normalized ? [normalized] : [];
}

function build_weight_lookup(weights = []) {
  const lookup = new Map();
  weights.forEach((item) => {
    const question_id = Number(item?.question_id || 0);
    const choices = Array.isArray(item?.choices) ? item.choices : [];
    choices.forEach((choice) => {
      lookup.set(`${question_id}:${String(choice?.option || '').trim()}`, Number(choice?.weight || 0));
    });
  });
  return lookup;
}

function build_choice_summary(question_id, weights = []) {
  const item = weights.find((weight_item) => Number(weight_item?.question_id || 0) === question_id);
  const choices = Array.isArray(item?.choices) ? item.choices : [];
  return choices.map((choice) => `${choice.option}: ${choice.weight || 0}`).join(', ');
}

function build_choice_rows(question_id, weights = [], selected = []) {
  const item = weights.find((weight_item) => Number(weight_item?.question_id || 0) === question_id);
  const choices = Array.isArray(item?.choices) ? item.choices : [];
  return choices.map((choice) => {
    const option = String(choice?.option || '').trim();
    return {
      option,
      weight: Number(choice?.weight || 0),
      is_selected: selected.includes(option)
    };
  });
}

function get_question_max_weight(question_id, weights = []) {
  const choices = build_choice_rows(question_id, weights);
  return choices.reduce((max, choice) => Math.max(max, choice.weight), 0);
}

function resolve_scored_answer(question_id, submitted_answers = {}, current_answers = new Map()) {
  const submitted_answer = Reflect.get(submitted_answers, String(question_id))
    || Reflect.get(submitted_answers, question_id);
  return current_answers.get(question_id) || submitted_answer;
}

function build_scored_answer_rows({
  questions = [],
  weights = [],
  submitted = {},
  current_answers = new Map()
}) {
  const answers = submitted?.answers || {};
  const weight_lookup = build_weight_lookup(weights);
  return questions.map((question) => {
    const question_id = Number(question?.question_id || question?.question || question?.id || 0);
    const selected = normalize_answer_values(resolve_scored_answer(question_id, answers, current_answers));
    const score = selected.reduce((total, answer) => total + (weight_lookup.get(`${question_id}:${answer}`) || 0), 0);
    const max_score = get_question_max_weight(question_id, weights);
    const comparison_percent = max_score > 0 ? Math.round((score / max_score) * 100) : 0;
    return {
      id: `risk-answer-${question_id}`,
      label: first_value([question?.question_text, question?.text, question?.label], 'Risk question'),
      selected_answer: selected.join(', ') || 'Not answered',
      selected_score: score,
      max_score,
      comparison_percent,
      choices: build_choice_rows(question_id, weights, selected),
      weighting: build_choice_summary(question_id, weights) || 'No weights configured',
      tone: score > 0 ? 'warning' : 'success'
    };
  });
}

function build_review_guidance(risk_item = {}) {
  if (risk_item.action === 'Auto-proceed') {
    return 'Risk is acceptable. Confirm the evidence and advance the stage when ready.';
  }

  if (risk_item.action === 'Monitor') {
    return 'Risk needs monitoring. Review the answers and request information if evidence is incomplete.';
  }

  if (risk_item.action === 'Resolve blockers') {
    return 'Resolve blockers before this onboarding can move forward.';
  }

  return 'Review the risk evidence, then advance, request information, or reject the onboarding.';
}

function build_current_answer_map(form_sections = []) {
  const answer_map = new Map();
  form_sections.forEach((form_section) => {
    (form_section?.sections || []).forEach((section) => {
      add_section_answers(answer_map, section);
    });
  });
  return answer_map;
}

function add_section_answers(answer_map, section = {}) {
  (section?.sub_sections || []).forEach((sub_section) => {
    (sub_section?.questions || []).forEach((question) => {
      add_question_answer(answer_map, question);
    });
  });
}

function add_question_answer(answer_map, question = {}) {
  const question_id = Number(question?.question_id || 0);
  const selected = normalize_answer_values(question?.answer);
  if (question_id && selected.length) answer_map.set(question_id, selected);
}

function get_first_text(values = [], fallback = '-') {
  const value = values.find((candidate) => candidate !== undefined && candidate !== null && String(candidate).trim() !== '');
  return value === undefined ? fallback : String(value);
}

function build_traversed_workflow_gate_ids(transition_history = []) {
  const logs = Array.isArray(transition_history) ? [...transition_history] : [];
  if (!logs.length) return [];

  const ordered_logs = logs.sort((left, right) => (
    new Date(left?.date_created || 0).getTime() - new Date(right?.date_created || 0).getTime()
  ));
  const gate_ids = [];
  const append_gate_id = function append_gate_id(status_name = '') {
    const gate_id = resolve_gate_id_from_stage_label(status_name);
    if (gate_id && !gate_ids.includes(gate_id)) {
      gate_ids.push(gate_id);
    }
  };

  append_gate_id(ordered_logs[0]?.from_status?.name || '');
  ordered_logs.forEach((log) => {
    append_gate_id(log?.to_status?.name || '');
  });
  return gate_ids;
}

export default {
  name: 'OnboardingRiskReview',
  components: {
    ApplicationDetailsReview,
    HistoricalVerificationChecksView,
    ManualVerificationHistoryView
  },
  data: () => ({
    active_filter: 'all',
    is_loading_risks: false,
    risks_error: '',
    selected_risk: null,
    active_detail_tab: 'summary',
    drawer_active_detail_tab: 'summary',
    is_detail_fullscreen: false,
    show_detail_drawer_on_page: false,
    ...WORKFLOW_ACTION_STATE,
    filters: [
      { label: 'All', count: '0', tone: 'all' },
      { label: 'Open', tone: 'open' },
      { label: 'Mine', tone: 'mine' },
      { label: 'Blocked', tone: 'blocked' }
    ],
    detail_tabs: [
      { id: 'risk', label: '1. Risk Review', icon: 'bi-exclamation-triangle' },
      { id: 'history', label: '2. History', icon: 'bi-clock-history' },
      { id: 'application', label: '3. Applicant Information', icon: 'bi-ui-checks-grid' }
    ],
    configured_gate_ids: [],
    workflow_transition_history: [],
    stage_timeline: [],
    questionnaire_answers: [],
    risk_summary_rows: [],
    risk_factor_items: [],
    risk_history_items: [],
    risk_history_loading: false,
    risk_history_error: '',
      risk_review_guidance: '',
      risk_assessment_name: '',
      summary_rows: [],
      profile_rows: [],
      contact_rows: [],
      case_form_sections: [],
      historical_gate_checks_by_gate: {},
      expanded_historical_verification_keys: [],
      risks: [],
      ...tablePaginationState()
    }),
  computed: {
    ...workflowActionComputed,
    active_journey_filter() {
      return resolve_onboarding_journey_filter_key(this.$route);
    },
    filtered_risks() {
      if (this.active_filter === 'blocked') {
        return this.filter_table_rows(
          this.risks.filter((item) => item.risk_tone === 'danger' || item.action !== 'Auto-proceed')
        );
      }

      if (this.active_filter === 'open') {
        return this.filter_table_rows(this.risks.filter((item) => item.action !== 'Auto-proceed'));
      }

      return this.filter_table_rows(this.risks);
    },
    table_total_rows() {
      return this.filtered_risks.length;
    },
    paginated_risks() {
      return this.paginate_table_rows(this.filtered_risks);
    },
    is_risk_detail_page() {
      return this.$route.name === 'OnboardingRiskReviewCase';
    },
    current_risk_gate_id() {
      return 'risk-review';
    },
    traversed_workflow_gate_ids() {
      return build_traversed_workflow_gate_ids(this.workflow_transition_history);
    },
    risk_journey_steps() {
      if (!this.selected_risk?.case_item) return [];

      const steps = this.filter_case_journey_steps(
        build_case_gate_journey(this.selected_risk.case_item, this.current_risk_gate_id),
        this.current_risk_gate_id
      );
      const current_index = steps.findIndex((step) => (
        String(step?.gate_id || '').trim().toLowerCase() === String(this.current_risk_gate_id || '').trim().toLowerCase()
      ));
      if (current_index < 0) {
        return steps;
      }

      const traversed_gate_ids = new Set(this.traversed_workflow_gate_ids);
      return steps.map((step, index) => ({
        ...step,
        state: index === current_index
          ? 'current'
          : index < current_index && (
            String(step?.state || '').trim().toLowerCase() === 'completed'
            || traversed_gate_ids.has(step.gate_id)
          )
            ? 'completed'
            : 'upcoming'
      }));
    },
    historical_gate_tabs() {
      const current_index = this.risk_journey_steps.findIndex((step) => step.state === 'current');
      if (current_index <= 0) return [];

      return this.risk_journey_steps
        .slice(0, current_index)
        .filter((step) => step.state === 'completed')
        .map((step) => ({
          id: `history-${step.gate_id}`,
          gate_id: step.gate_id,
          label: step.label,
          badge: 'Completed'
        }));
    },
    detail_application_id() {
      return Number(this.$route.params.applicationId || 0);
    },
    detail_tabs_with_history() {
      const tabs = [];
      tabs.push({
        id: 'risk',
        label: 'Required Action: Risk Assessment',
        icon: 'bi-exclamation-triangle',
        badge: 'Current'
      });
      tabs.push({
        id: 'application',
        label: 'Application Reference',
        icon: 'bi-ui-checks-grid',
        badge: 'Reference'
      });
      this.historical_gate_tabs.forEach((tab) => {
        tabs.push({ ...tab, icon: 'bi-clock-history' });
      });
      tabs.push({ id: 'history', label: 'Risk History', icon: 'bi-clock-history' });
      return tabs;
    },
    detail_reference_tab() {
      return this.detail_tabs_with_history.find((tab) => /application reference/i.test(tab.label || '')) || null;
    },
    detail_history_tabs() {
      return this.detail_tabs_with_history.filter((tab) => tab.gate_id);
    },
    detail_shell_tabs() {
      return [
        { id: 'action', label: 'Required Action', badge: 'Current' },
        { id: 'reference', label: 'Application Reference', badge: null },
        { id: 'history', label: 'Onboarding History', badge: this.detail_history_tabs.length ? String(this.detail_history_tabs.length) : null }
      ];
    },
    active_detail_shell_tab() {
      if (this.active_detail_tab === 'application') {
        return 'reference';
      }

      if (this.active_detail_tab === 'case-history' || this.active_historical_gate_tab) {
        return 'history';
      }

      return 'action';
    },
    risk_workspace_tabs() {
      const workspace_tab_labels = {
        risk: 'Required Action',
        history: 'Risk History'
      };

      return this.detail_tabs_with_history
        .filter((tab) => Object.hasOwn(workspace_tab_labels, tab.id))
        .map((tab) => ({
          ...tab,
          workspace_label: workspace_tab_labels[tab.id]
        }));
    },
    active_historical_gate_tab() {
      return this.historical_gate_tabs.find((tab) => tab.id === this.active_detail_tab) || null;
    },
    should_show_detail_drawer() {
      return Boolean(this.selected_risk && (!this.is_risk_detail_page || this.show_detail_drawer_on_page));
    },
    drawer_detail_tabs() {
      const tabs = [{ id: 'summary', label: 'Summary', icon: 'bi-grid' }];
      this.case_form_sections.forEach((form) => {
        (Array.isArray(form?.sections) ? form.sections : []).forEach((section) => {
          tabs.push({
            id: this.drawer_tab_id_for_section(form, section),
            label: section?.label || form?.form_name || 'Section',
            icon: this.drawer_tab_icon_for_section(form, section)
          });
        });
      });
      return tabs;
    },
    active_drawer_form_section() {
      for (const form of this.case_form_sections) {
        const matched_section = (Array.isArray(form?.sections) ? form.sections : [])
          .find((section) => this.drawer_tab_id_for_section(form, section) === this.drawer_active_detail_tab);
        if (matched_section) return matched_section;
      }
      return null;
    },
    risk_has_next_status() {
      const current_index = this.stage_timeline.findIndex((item) => item?.status === 'current');
      if (current_index < 0) return false;

      return this.stage_timeline
        .slice(current_index + 1)
        .some((item) => item?.status === 'upcoming');
    },
    should_show_risk_advance_stage() {
      return this.can_advance_stage || this.risk_has_next_status;
    },
    ...tablePaginationComputed
  },
  mounted() {
    this.load_risks();
    window.addEventListener('onboarding-data-updated', this.handle_onboarding_data_updated);
  },
  beforeUnmount() {
    window.removeEventListener('onboarding-data-updated', this.handle_onboarding_data_updated);
  },
  watch: {
    active_journey_filter() {
      this.close_risk_detail();
      this.load_risks();
    },
    detail_application_id() {
      this.sync_risk_detail_from_route();
    },
    '$route.name'(route_name) {
      if (route_name === 'OnboardingRiskReview') {
        this.close_risk_detail();
      }
    }
  },
  methods: {
    ...workflowActionMethods,
    handle_onboarding_data_updated() {
      this.load_risks();
    },
    get_workflow_action_case() {
      return this.selected_risk?.case_item || null;
    },
    async on_workflow_action_success() {
      this.close_risk_detail();
      await this.load_risks();
    },
    async run_risk_advance_stage() {
      if (!this.can_advance_stage && this.selected_risk?.case_item) {
        await this.load_workflow_actions_for_case(this.selected_risk.case_item);
      }
      await this.run_selected_workflow_action('advance');
    },
    derive_risk_score(case_item = {}) {
      const explicit_score = Number(first_value([
        case_item?.risk_score,
        case_item?.risk_rating_score,
        case_item?.threshold_assessment?.score,
        case_item?.threshold_assessment?.risk_score
      ], Number.NaN));

      if (Number.isFinite(explicit_score)) {
        return String(Math.max(0, Math.min(100, Math.round(explicit_score))));
      }

      const risk_state = RISK_STATES.get(case_item.risk_tone) || DEFAULT_RISK_STATE;
      return String(risk_state.score);
    },
    resolve_risk_action(case_item = {}) {
      if (case_item.activation_tone === 'blocked') return 'Resolve blockers';
      if (case_item.risk_tone === 'high') return 'Review';
      if (case_item.risk_tone === 'medium') return 'Monitor';
      return 'Auto-proceed';
    },
    map_case_to_risk(case_item) {
      const risk_state = RISK_STATES.get(case_item.risk_tone) || DEFAULT_RISK_STATE;

      return Object.assign({}, {
        case_name: case_item.name,
        journey: case_item.journey,
        risk: case_item.risk,
        risk_tone: risk_state.risk_tone,
        score: this.derive_risk_score(case_item),
        stage: get_stage_label(case_item),
        action: this.resolve_risk_action(case_item) || risk_state.action,
        case_item,
        stage_timeline: build_stage_timeline(case_item),
        questionnaire_answers: build_questionnaire_answers(case_item)
      });
    },
    async load_risks() {
      this.is_loading_risks = true;
      this.risks_error = '';
      show_onboarding_system_loader('Loading risk review queue...');

      try {
        const cases = await fetch_all_onboarding_cases();
        this.risks = cases
          .filter((case_item) => matches_active_journey(case_item, this.active_journey_filter))
          .filter((case_item) => is_risk_stage(case_item))
          .map((case_item) => this.map_case_to_risk(case_item));
        this.filters = this.filters.map((filter) => (
          filter.tone === 'all' ? { ...filter, count: String(this.risks.length) } : filter
        ));
        await this.sync_risk_detail_from_route();
      } catch (error) {
        this.risks = [];
        this.risks_error = error?.response?.data?.message || error?.message || 'Unable to load risk review queue.';
      } finally {
        this.is_loading_risks = false;
        hide_onboarding_system_loader_after_render(this);
      }
    },
    set_active_filter(tone) {
      this.active_filter = tone;
      this.reset_table_page();
    },
    ...tablePaginationMethods,
    open_risk_page(risk_item = {}) {
      const application_id = Number(risk_item?.case_item?.application_id || 0);
      if (!application_id) {
        this.open_risk_detail(risk_item);
        return;
      }
      this.$router.push({
        name: 'OnboardingRiskReviewCase',
        params: { applicationId: String(application_id) },
        query: this.$route.query
      });
    },
    open_risk_page_drawer() {
      if (!this.is_risk_detail_page || !this.selected_risk) return;
      this.show_detail_drawer_on_page = true;
      this.is_detail_fullscreen = false;
      this.drawer_active_detail_tab = 'summary';
    },
    close_risk_drawer() {
      if (this.is_risk_detail_page && this.show_detail_drawer_on_page) {
        this.show_detail_drawer_on_page = false;
        this.is_detail_fullscreen = false;
        return;
      }
      this.close_risk_detail();
    },
    async sync_risk_detail_from_route() {
      if (!this.is_risk_detail_page || !this.detail_application_id) {
        this.configured_gate_ids = [];
        this.workflow_transition_history = [];
        return;
      }
      const matched_risk = this.risks.find((risk_item) => (
        Number(risk_item?.case_item?.application_id || 0) === this.detail_application_id
      ));
      if (!matched_risk) {
        this.configured_gate_ids = [];
        this.workflow_transition_history = [];
        return;
      }
      this.apply_risk_detail(matched_risk);
        this.active_detail_tab = 'risk';
        try {
          const hydrated_case = await hydrate_case_detail(matched_risk.case_item);
          this.apply_risk_detail(this.map_case_to_risk(hydrated_case), hydrated_case);
          this.configured_gate_ids = await fetch_configured_case_gate_ids(hydrated_case).catch(() => []);
          this.workflow_transition_history = await fetch_application_transition_history(
            Number(hydrated_case?.application_id || hydrated_case?.id || 0)
          ).catch(() => []);
          await this.load_historical_gate_contexts(hydrated_case);
          this.sync_active_detail_tab();
          await this.hydrate_created_risk_assessment(hydrated_case);
          await this.load_risk_history_for_case(hydrated_case);
          await this.load_workflow_actions_for_case(hydrated_case);
        } catch {
          this.configured_gate_ids = await fetch_configured_case_gate_ids(matched_risk.case_item).catch(() => []);
          this.workflow_transition_history = await fetch_application_transition_history(
            Number(matched_risk.case_item?.application_id || matched_risk.case_item?.id || 0)
          ).catch(() => []);
          await this.load_workflow_actions_for_case(matched_risk.case_item);
        }
      },
    apply_risk_detail(risk_item, case_item = risk_item?.case_item || {}) {
      this.selected_risk = risk_item;
      this.stage_timeline = risk_item.stage_timeline || [];
      this.questionnaire_answers = risk_item.questionnaire_answers || [];
      this.risk_summary_rows = build_risk_summary_rows(case_item, risk_item);
      this.risk_factor_items = build_risk_factors(case_item);
      this.risk_review_guidance = build_review_guidance(risk_item);
      this.summary_rows = build_summary_rows(case_item);
      this.profile_rows = build_profile_rows(case_item);
      this.contact_rows = build_contact_rows(case_item);
      this.case_form_sections = build_case_form_sections(case_item);
    },
    filter_case_journey_steps(steps = [], current_gate_id = '') {
      const source_steps = Array.isArray(steps) ? steps : [];
      if (!source_steps.length) {
        return [];
      }

      const normalized_current_gate_id = String(current_gate_id || '').trim().toLowerCase();
      const current_index = source_steps.findIndex((step) => (
        String(step?.gate_id || '').trim().toLowerCase() === normalized_current_gate_id
      ));
      if (current_index < 0) {
        return source_steps;
      }

      return source_steps.map((step, index) => ({
        ...step,
        state: index < current_index ? 'completed' : index === current_index ? 'current' : 'upcoming'
      }));
    },
    sync_active_detail_tab() {
      const available_tab_ids = this.detail_tabs_with_history.map((tab) => tab.id);
      if (this.active_detail_tab !== 'case-history' && !available_tab_ids.includes(this.active_detail_tab)) {
        this.active_detail_tab = 'risk';
      }
    },
    open_gate_journey_step(step = {}) {
      if (step?.state !== 'completed' || !step?.detail_route_name || !this.selected_risk?.case_item) {
        return;
      }

      const application_id = Number(this.selected_risk.case_item?.application_id || 0);
      if (!application_id) {
        return;
      }

      this.$router.push({
        name: step.detail_route_name,
        params: { applicationId: String(application_id) },
        query: this.$route.query
      });
    },
    async load_historical_gate_contexts(case_item = {}) {
      this.historical_gate_checks_by_gate = {};
      if (!this.historical_gate_tabs.length) {
        return;
      }

      const entity_profile_id = Number(case_item?.entity_profile_id || case_item?.profile?.id || 0);
      const forms = get_case_forms(case_item);
      const entity_type_id = get_case_entity_type_id(case_item, case_item);
      const binding_options = build_binding_options(case_item);
      const fetched_data = await Promise.all(build_load_promises(
        entity_profile_id,
        entity_type_id,
        binding_options,
        forms
      ));
      fetched_data[1] = apply_case_manual_binding_fallback(fetched_data[1], case_item);
      const manual_mapping_by_link = await build_manual_mapping_by_link(
        fetched_data[1]?.manual_bindings
      );
      const checks = await enrich_manual_checks_with_run_mappings(
        build_case_detail_checks(case_item, forms, fetched_data, manual_mapping_by_link)
      );
      const runtime_manual_checks = await build_initial_case_checks([case_item], true);
      const enriched_runtime_manual_checks = await enrich_manual_checks_with_run_mappings(runtime_manual_checks);
      const effective_manual_checks = checks.some(check_has_manual_mappings)
        ? checks
        : [
            ...checks,
            ...enriched_runtime_manual_checks.filter((check) => check?.verification_mode === 'manual')
          ];
      const next_checks_by_gate = {};
      this.historical_gate_tabs.forEach((tab) => {
        if (tab.gate_id === 'manual-verification') {
          next_checks_by_gate[tab.gate_id] = effective_manual_checks.filter((check) => check?.verification_mode === 'manual');
          return;
        }
        if (tab.gate_id === 'verification') {
          next_checks_by_gate[tab.gate_id] = checks.filter((check) => check?.verification_mode !== 'manual' && !check?.is_gate_placeholder);
          return;
        }
        next_checks_by_gate[tab.gate_id] = [];
      });
      this.historical_gate_checks_by_gate = next_checks_by_gate;
    },
    active_historical_gate_checks() {
      if (!this.active_historical_gate_tab) return [];
      return this.historical_gate_checks_by_gate[this.active_historical_gate_tab.gate_id] || [];
    },
    active_historical_transition_log() {
      const gate_id = String(this.active_historical_gate_tab?.gate_id || '').trim().toLowerCase();
      if (!gate_id) return null;

      return [...(Array.isArray(this.workflow_transition_history) ? this.workflow_transition_history : [])]
        .filter((log) => resolve_gate_id_from_stage_label(log?.to_status?.name || '') === gate_id)
        .sort((left, right) => new Date(right?.date_created || 0).getTime() - new Date(left?.date_created || 0).getTime())[0] || null;
    },
    historical_gate_action_label() {
      return get_first_text([
        this.active_historical_transition_log()?.action_taken,
        this.active_historical_transition_log()?.to_status?.name
      ], 'Completed');
    },
    historical_gate_actor() {
      return get_first_text([
        this.active_historical_transition_log()?.reviewer,
        this.active_historical_transition_log()?.role
      ], 'System');
    },
    historical_gate_reason() {
      return get_first_text([
        this.active_historical_transition_log()?.note
      ], 'No reason was recorded for this gate.');
    },
    get_case_forms_for_check(check_item = {}) {
      const selected_case_forms = Array.isArray(this.selected_risk?.case_item?.detailed_forms)
        ? this.selected_risk.case_item.detailed_forms
        : [];
      if (selected_case_forms.length) return selected_case_forms;

      const selected_fallback_forms = Array.isArray(this.selected_risk?.case_item?.forms)
        ? this.selected_risk.case_item.forms
        : [];
      if (selected_fallback_forms.length) return selected_fallback_forms;

      const check_case_forms = Array.isArray(check_item?.case_item?.detailed_forms)
        ? check_item.case_item.detailed_forms
        : [];
      if (check_case_forms.length) return check_case_forms;

      return Array.isArray(check_item?.case_item?.forms) ? check_item.case_item.forms : [];
    },
    status_badge_class(tone) {
      return manageVerificationSupportMethods.status_badge_class.call(this, tone);
    },
    field_result_row_class(match) {
      return manageVerificationSupportMethods.field_result_row_class.call(this, match);
    },
    evidence_icon_class(tone) {
      return manageVerificationSupportMethods.evidence_icon_class.call(this, tone);
    },
    format_field_label(value = '') {
      return manageVerificationSupportMethods.format_field_label.call(this, value);
    },
    format_detail_value(value) {
      return manageVerificationSupportMethods.format_detail_value.call(this, value);
    },
    get_check_field_results(item = {}) {
      return manageVerificationResultMethods.get_check_field_results.call(this, item);
    },
    get_automatic_result_summary(item = {}) {
      return manageVerificationResultMethods.get_automatic_result_summary.call(this, item);
    },
    get_expected_verification_fields(item = {}) {
      return manageVerificationResultMethods.get_expected_verification_fields.call(this, item);
    },
    get_check_result_detail_rows(item = {}) {
      return manageVerificationResultMethods.get_check_result_detail_rows.call(this, item);
    },
    get_check_passed_chips(item = {}) {
      return manageVerificationResultMethods.get_check_passed_chips.call(this, item);
    },
    get_check_history(item = {}) {
      return manageVerificationResultMethods.get_check_history.call(this, item);
    },
    historical_verification_check_expanded(item = {}) {
      return this.expanded_historical_verification_keys.includes(String(item?.id || ''));
    },
    toggle_historical_verification_check_expanded(item = {}) {
      const target_key = String(item?.id || '');
      if (!target_key) return;

      if (this.historical_verification_check_expanded(item)) {
        this.expanded_historical_verification_keys = this.expanded_historical_verification_keys
          .filter((key) => key !== target_key);
        return;
      }

      this.expanded_historical_verification_keys = [
        ...this.expanded_historical_verification_keys,
        target_key
      ];
    },
    active_historical_manual_item() {
      if (this.active_historical_gate_tab?.gate_id !== 'manual-verification') return null;
      return build_historical_manual_item(
        this.active_historical_gate_checks().filter((check) => check?.verification_mode === 'manual')
      );
    },
    historical_manual_status_label() {
      const manual_item = this.active_historical_manual_item();
      return get_first_text([
        get_manual_outcome_label(manual_item),
        manual_item?.manual_run?.status,
        manual_item?.status
      ], 'Completed');
    },
    historical_manual_status_tone() {
      const manual_item = this.active_historical_manual_item();
      return first_value([
        get_manual_outcome_tone(manual_item),
        manual_item?.manual_run?.status_tone,
        manual_item?.status_tone
      ], 'secondary');
    },
    historical_manual_reviewer() {
      const manual_item = this.active_historical_manual_item();
      return get_first_text([
        manual_item?.manual_run?.reviewer_name,
        manual_item?.manual_run?.reviewer,
        manual_item?.raw_status?.reviewer_name,
        manual_item?.raw_status?.reviewer,
        manual_item?.raw_status?.updated_by_name
      ], 'Reviewed');
    },
    historical_manual_timestamp() {
      const manual_item = this.active_historical_manual_item();
      return get_first_text([
        manual_item?.last_run,
        manual_item?.manual_run?.completed_at,
        manual_item?.manual_run?.started_at
      ], '-');
    },
    historical_manual_comment() {
      const manual_item = this.active_historical_manual_item();
      return get_first_text([
        manual_item?.manual_run?.decision_reason,
        manual_item?.raw_status?.decision_reason,
        manual_item?.raw_status?.reviewer_comment
      ], 'No reviewer comments were recorded for this gate.');
    },
    historical_manual_field_runs() {
      const manual_item = this.active_historical_manual_item();
      return manual_item ? get_manual_field_runs(manual_item) : [];
    },
    historical_manual_decision_action() {
      return get_manual_outcome_action(this.active_historical_manual_item());
    },
    historical_manual_decision_label() {
      const action = this.historical_manual_decision_action();
      if (action === 'approve') return 'Approved';
      if (action === 'request_changes') return 'Changes requested';
      if (action === 'reject') return 'Rejected';
      return this.historical_manual_status_label();
    },
    historical_manual_decision_tone() {
      const action = this.historical_manual_decision_action();
      if (action === 'approve') return 'success';
      if (action === 'request_changes') return 'warning';
      if (action === 'reject') return 'danger';
      return 'secondary';
    },
    historical_manual_review_sections() {
      const manual_item = this.active_historical_manual_item();
      if (!manual_item) return [];

      const source_sections = build_historical_manual_review_sections(manual_item, this.case_form_sections);
      const field_run_by_question = build_field_run_by_question(this.historical_manual_field_runs());
      return source_sections
        .map((form_section) => decorate_review_form_section(form_section, field_run_by_question))
        .filter((form_section) => Array.isArray(form_section?.sections) && form_section.sections.length);
    },
    historical_gate_status_label() {
      return get_first_text([
        this.active_historical_transition_log()?.to_status?.name,
        this.active_historical_gate_checks()[0]?.status
      ], 'Completed');
    },
    historical_gate_status_tone() {
      const label = String(this.historical_gate_status_label() || '').trim().toLowerCase();
      if (label.includes('reject')) return 'danger';
      if (label.includes('request') || label.includes('change')) return 'warning';
      if (label.includes('approve') || label.includes('complete')) return 'success';
      return first_value([this.active_historical_gate_checks()[0]?.status_tone], 'secondary');
    },
    historical_gate_completed_at() {
      return get_first_text([
        this.active_historical_transition_log()?.date_created,
        ...this.active_historical_gate_checks().map((check) => check?.last_run).filter(Boolean)
      ], '-');
    },
    historical_gate_summary_text() {
      if (this.active_historical_gate_tab?.gate_id === 'verification') {
        return 'Review the verification results returned before this applicant entered risk review.';
      }
      if (this.active_historical_gate_tab?.gate_id === 'manual-verification') {
        return 'Review the manual verification outcome captured before this applicant entered risk review.';
      }
      return 'Read-only context carried forward from completed gates.';
    },
    async open_risk_detail(risk_item) {
      show_onboarding_system_loader('Loading risk drawer...');
      this.reset_workflow_action_state();
      this.apply_risk_detail(risk_item);
      this.drawer_active_detail_tab = 'summary';
      this.is_detail_fullscreen = false;

        try {
          const hydrated_case = await hydrate_case_detail(risk_item.case_item);
          const risk_case = hydrated_case;
          this.apply_risk_detail(this.map_case_to_risk(risk_case), risk_case);
          this.workflow_transition_history = await fetch_application_transition_history(
            Number(risk_case?.application_id || risk_case?.id || 0)
          ).catch(() => []);
          await this.load_historical_gate_contexts(risk_case);
          this.sync_active_detail_tab();
          await this.hydrate_created_risk_assessment(risk_case);
          await this.load_risk_history_for_case(risk_case);
          await this.load_workflow_actions_for_case(risk_case);
      } catch {
        // Keep the initially selected live case if hydration fails.
        await this.load_workflow_actions_for_case(risk_item.case_item);
      } finally {
        hide_onboarding_system_loader_after_render(this);
      }
    },
    close_risk_detail() {
      this.reset_workflow_action_state();
      this.show_detail_drawer_on_page = false;
        this.selected_risk = null;
        this.workflow_transition_history = [];
        this.summary_rows = [];
        this.profile_rows = [];
        this.contact_rows = [];
        this.case_form_sections = [];
        this.historical_gate_checks_by_gate = {};
        this.expanded_historical_verification_keys = [];
        this.risk_summary_rows = [];
      this.risk_factor_items = [];
      this.risk_history_items = [];
      this.risk_history_loading = false;
      this.risk_history_error = '';
      this.risk_review_guidance = '';
      this.risk_assessment_name = '';
      this.active_detail_tab = 'summary';
      this.drawer_active_detail_tab = 'summary';
      this.is_detail_fullscreen = false;
    },
    async hydrate_created_risk_assessment(case_item = {}) {
      const assessment = resolve_created_assessment(case_item);
      const entity_id = Number(case_item?.entity_profile_id || 0);
      if (!assessment || !entity_id) return;

      const [questions, weights, submitted] = await Promise.all([
        fetch_assigned_questions_to_assessment(assessment.id).catch(() => []),
        fetch_risk_checklist_weights(assessment.id).catch(() => []),
        fetch_submitted_risk_assessment({ entity_id, assessment_id: assessment.id }).catch(() => null)
      ]);
      const submitted_result = submitted || case_item?.risk_result || {};
      this.risk_assessment_name = assessment.name;
      this.risk_summary_rows = this.build_created_risk_summary_rows(assessment, submitted_result, case_item);
      this.risk_factor_items = build_scored_answer_rows({
        questions,
        weights,
        submitted: submitted_result,
        current_answers: build_current_answer_map(this.case_form_sections)
      });
      this.risk_review_guidance = build_review_guidance(this.selected_risk || {});
    },
    async load_risk_history_for_case(case_item = {}) {
      const application_id = Number(case_item?.application_id || this.detail_application_id || 0);
      const entity_id = Number(case_item?.entity_profile_id || 0) || null;
      if (!application_id) return;

      this.risk_history_loading = true;
      this.risk_history_error = '';
      try {
        this.risk_history_items = await fetch_application_risk_history({ application_id, entity_id });
      } catch (error) {
        this.risk_history_items = [];
        this.risk_history_error = error?.response?.data?.message || error?.message || 'Unable to load risk history.';
      } finally {
        this.risk_history_loading = false;
      }
    },
    build_created_risk_summary_rows(assessment, submitted = {}, case_item = {}) {
      const rows = [
        { label: 'Assessment', value: assessment.name },
        { label: 'Applicant score', value: first_value([submitted?.total_score, case_item?.risk_score], '-') },
        { label: 'Risk level', value: first_value([submitted?.risk_level, case_item?.risk_level, case_item?.risk], '-') },
        { label: 'Auto decision', value: first_value([submitted?.auto_decision, submitted?.outcome], '-') },
        { label: 'Stage', value: get_stage_label(case_item) || '-' },
        { label: 'Reviewer action', value: this.selected_risk?.action || 'Review' }
      ];
      return rows;
    },
    risk_summary_value(label, fallback = '-') {
      const matched_row = this.risk_summary_rows.find((row) => row.label === label);
      return matched_row?.value || fallback;
    },
    total_possible_risk_weight() {
      return this.risk_factor_items.reduce((total, item) => total + Number(item.max_score || 0), 0);
    },
    applicant_risk_score_value() {
      if (this.risk_factor_items.length) {
        return this.risk_factor_items.reduce((total, item) => total + Number(item.selected_score || 0), 0);
      }
      const summary_score = Number(this.risk_summary_value('Applicant score', Number.NaN));
      if (Number.isFinite(summary_score)) return summary_score;
      return 0;
    },
    applicant_risk_score_percent() {
      const total_possible = this.total_possible_risk_weight();
      if (!total_possible) return 0;
      return Math.round((this.applicant_risk_score_value() / total_possible) * 100);
    },
    format_risk_history_date(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '-';
      return date.toLocaleString();
    },
    risk_history_scope_label(item = {}) {
      return item.is_application_scoped ? 'Application run' : 'Legacy entity run';
    },
    risk_history_badge_class(level) {
      const normalized_level = normalize_text(level);
      if (normalized_level.includes('high')) return 'bg-danger-subtle text-danger-emphasis';
      if (normalized_level.includes('medium') || normalized_level.includes('moderate')) return 'bg-warning-subtle text-warning-emphasis';
      if (normalized_level.includes('low')) return 'bg-success-subtle text-success-emphasis';
      return 'bg-secondary-subtle text-secondary-emphasis';
    },
    set_detail_tab(tab_id) {
      this.active_detail_tab = tab_id;
    },
    set_detail_shell_tab(tab_id = 'action') {
      if (tab_id === 'reference') {
        this.set_detail_tab(this.detail_reference_tab?.id || 'application');
        return;
      }

      if (tab_id === 'history') {
        this.set_detail_tab(this.detail_history_tabs[0]?.id || 'case-history');
        return;
      }

      this.set_detail_tab(this.current_detail_action_tab()?.id || 'risk');
    },
    current_detail_action_tab() {
      return this.detail_tabs_with_history.find((tab) => String(tab?.badge || '').trim().toLowerCase() === 'current') || this.detail_tabs_with_history[0] || null;
    },
    current_detail_action_copy() {
      return 'Assess the applicant risk evidence, confirm the recommended outcome, and decide whether to advance, request info, or reject.';
    },
    set_drawer_detail_tab(tab_id) {
      this.drawer_active_detail_tab = tab_id;
    },
    open_selected_risk_profile() {
      const first_section_tab = this.drawer_detail_tabs.find((tab) => tab.id !== 'summary');
      this.drawer_active_detail_tab = first_section_tab?.id || 'summary';
    },
    drawer_tab_id_for_section(form, section) {
      return `form-${form?.id || form?.form_name || 'section'}-${section?.id || section?.label || 'item'}`;
    },
    drawer_tab_icon_for_section(form = {}, section = {}) {
      const label = normalize_text(`${form?.form_name || ''} ${section?.label || ''}`);
      return ['document', 'upload', 'attachment', 'file', 'evidence'].some((word) => label.includes(word))
        ? 'bi-folder'
        : 'bi-list-check';
    },
    toggle_detail_fullscreen() {
      this.is_detail_fullscreen = !this.is_detail_fullscreen;
    },
    risk_badge_class(tone) {
      if (tone === 'danger') return 'bg-danger-subtle text-danger-emphasis';
      if (tone === 'success') return 'bg-success-subtle text-success-emphasis';
      if (tone === 'warning') return 'bg-warning-subtle text-warning-emphasis';
      return 'bg-secondary-subtle text-secondary-emphasis';
    },
    activation_badge_class() {
      if (this.selected_risk?.case_item?.activation_tone === 'ready') {
        return 'bg-success-subtle text-success-emphasis';
      }

      if (this.selected_risk?.case_item?.activation_tone === 'blocked') {
        return 'bg-danger-subtle text-danger-emphasis';
      }

      return 'bg-warning-subtle text-warning-emphasis';
    },
    selected_case_subtitle() {
      if (!this.selected_risk?.case_item) return '';

      const owner = first_value([
        this.selected_risk.case_item.owner,
        this.selected_risk.case_item.onboarding_owner_role_name
      ], 'Unassigned');
      const risk = first_value([this.selected_risk.risk], 'Unknown risk');
      const eta = first_value([
        this.selected_risk.case_item.eta,
        this.selected_risk.case_item.onboarding_activate_by
      ], 'No SLA date');

      return [owner, `${risk} risk`, `ETA ${eta}`].filter(Boolean).join(' - ');
    },
    selected_case_context() {
      if (!this.selected_risk?.case_item) return '';

      return [
        this.selected_risk.case_item.journey || 'Onboarding',
        normalize_text(this.selected_risk.case_item.stage || '').includes('risk') ? 'Risk gate' : (this.selected_risk.case_item.stage || 'Current gate')
      ].filter(Boolean).join(' - ');
    }
  }
};
